/**
 * Guideline-level human review form shown after every criterion of a guideline
 * has been reviewed (the per-guideline summary step).
 *
 * Captures the reviewer's overall met/not-met verdict, the reasoning behind it,
 * and free-text notes on how the AI could get the guideline right next time.
 * Fully controlled by the parent via {@link GuidelineReviewInput}.
 * @module GuidelineFeedback
 */
import React from "react";
import {
  EvaluationResult,
  HumanVerdict,
} from "../../../data-model/nccnGuidelines";
import { GuidelineReviewInput } from "../utils/nccnReviewFeedback";

/** Props for {@link GuidelineFeedback}. */
interface GuidelineFeedbackProps {
  /** The current guideline-level review values. */
  value: GuidelineReviewInput;
  /** Called with the next review values whenever a field changes. */
  onChange: (value: GuidelineReviewInput) => void;
  /** The guideline's AI evaluation result (Met / Not Met / Undetermined). */
  aiResult: EvaluationResult | null | undefined;
  /** Whether the inputs should be disabled (e.g. while persisting). */
  disabled?: boolean;
}

/** The selectable verdict options, in display order. */
const VERDICT_OPTIONS: { label: string; value: HumanVerdict }[] = [
  { label: "Met", value: HumanVerdict.Met },
  { label: "Not Met", value: HumanVerdict.NotMet },
];

const LABEL_CLASS =
  "text-[11px] font-normal leading-[18px] text-[#1F1F1F] " +
  "[font-kerning:none] [font-feature-settings:'ss01']";

const INPUT_CLASS =
  "h-10 w-full rounded border border-[#D6D6D6] bg-white px-3 text-[13px] " +
  "font-normal leading-5 text-[#0F0F0F] outline-none " +
  "placeholder:text-[#8C8C8C] focus:border-[#0F0F0F]";

/**
 * Renders the guideline-level feedback form.
 *
 * @param props - Current review values plus the change and disabled flags
 * @returns The rendered form
 */
export const GuidelineFeedback: React.FC<GuidelineFeedbackProps> = ({
  value,
  onChange,
  aiResult,
  disabled = false,
}) => {
  // Map the AI result onto a met/not-met verdict for comparison. An
  // Undetermined result has no equivalent human verdict, so any recorded verdict
  // counts as differing — which is what surfaces the AI-improvement field.
  const aiVerdict =
    aiResult === "True"
      ? HumanVerdict.Met
      : aiResult === "False"
        ? HumanVerdict.NotMet
        : null;
  const verdictDiffersFromAi =
    value.verdict !== null && value.verdict !== aiVerdict;

  return (
    <div className="flex flex-col gap-5 self-stretch">
      <fieldset className="flex flex-col gap-2">
        <legend className={LABEL_CLASS}>Your Verdict</legend>
        <div className="flex items-center gap-6">
          {VERDICT_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="mt-1 flex cursor-pointer items-center gap-2"
            >
              <input
                type="radio"
                name="guideline-verdict"
                value={option.value}
                checked={value.verdict === option.value}
                disabled={disabled}
                onChange={() => onChange({ ...value, verdict: option.value })}
                className="h-4 w-4 accent-[#0056D6]"
              />
              <span className="text-[12px] font-normal leading-[18px] tracking-[0.2px] text-[#0F0F0F]">
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-col gap-2 self-stretch">
        <label htmlFor="guideline-verdict-reasoning" className={LABEL_CLASS}>
          Reasoning of Human Verdict
          <span className="text-[#CC0300]"> *</span>
        </label>
        <input
          id="guideline-verdict-reasoning"
          type="text"
          className={INPUT_CLASS}
          placeholder="Enter your Reasoning here"
          value={value.verdictReasoning}
          disabled={disabled}
          onChange={(event) =>
            onChange({ ...value, verdictReasoning: event.target.value })
          }
        />
      </div>

      {verdictDiffersFromAi && (
        <div className="flex flex-col gap-2 self-stretch">
          <label htmlFor="guideline-ai-improvement" className={LABEL_CLASS}>
            What would help the AI get this right next time?
          </label>
          <input
            id="guideline-ai-improvement"
            type="text"
            className={INPUT_CLASS}
            placeholder="Enter your Reasoning here"
            value={value.aiImprovementNotes}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...value, aiImprovementNotes: event.target.value })
            }
          />
        </div>
      )}
    </div>
  );
};
